import { ArrowRightIcon, Button, Table, TableColumns } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import {
  BillingMethods,
  use_ContractsQuery,
  useAdditionalTeachingLoadsListQuery,
  useListPlannedTeacherTeachingLoadQuery,
} from '../../../types/planung-graphql-client-defs';
import { sumUpArray } from '../../../utils/arrayFunc';
import { formatNumber } from '../../../utils/helper';
import { _evalBillingMethod } from '../../Lessons/Tables/Teachers/utils/eval-billing-method';

export type DeputatesTableType = {
  teacherUuid: string;
  name: string;
  teachingBlockWeeks: number;
  teachingBlocks: number;
  deputateSubjectContainer: number;
  deputateLessonUnit: number;
  deputateAdditional: number;
  contractHours: number;
  billedHours: number;
};

type PlannedDeputatesTableProps = {
  onNavigate: (teacherUuid: string) => void;
};

export const PlannedDeputatesTable = observer(({ onNavigate }: PlannedDeputatesTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const atlContext = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('deputate-list');
  const { sorting, saveSorting } = useColumnsSort('deputate-list');

  const [{ data: deputateData }] = useListPlannedTeacherTeachingLoadQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      schoolYearUuid: currentSchoolYear?.uuid ?? '',
    },
    pause: !currentSchoolYear || currentSchoolYear.uuid === '',
  });

  const { teacherData } = useLoadBasicData({ pause: !currentSchoolYear || currentSchoolYear.uuid === '' });

  const [{ data: contractData }] = use_ContractsQuery({
    variables: {
      where: {
        person: {
          uuid_IN: teacherData?.people.map((p) => p.uuid) ?? [],
        },
      },
    },
    pause: !teacherData || teacherData.people.length === 0,
  });

  const [{ data: additionalTeachingLoadQueryResult }] = useAdditionalTeachingLoadsListQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      schoolyear: currentSchoolYear?.uuid ?? '',
    },
    pause: !currentSchoolYear || currentSchoolYear.uuid === '',
    context: atlContext,
  });

  const fullTimeHours = useUserConfigContext().selectedSchoolYear?.fullTimeHours;

  const data: DeputatesTableType[] =
    teacherData?.people.map((person) => {
      const lessonsForPerson =
        deputateData?.plannedTeachingLoadByTeacher.filter((lesson) => lesson.teacherUuid === person.uuid) ?? [];

      const deputateAdditional =
        sumUpArray(
          additionalTeachingLoadQueryResult?.additionalTeachingLoads
            ?.filter((l) => l.teacher.uuid === person.uuid)
            .map((a) => a.hours) ?? [],
        ) ?? 0;

      const deputateSubjectContainer = lessonsForPerson
        .filter((lesson) => lesson.subjectContainerUuid !== undefined && lesson.subjectContainerUuid !== null)
        .map((lesson) => lesson.deputat ?? 0)
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

      const teachingBlocks = sumUpArray(lessonsForPerson.map((lesson) => lesson.teachingBlocks ?? 0));
      const teachingBlockWeeks = sumUpArray(lessonsForPerson.map((lesson) => lesson.teachingBlockWeeks ?? 0));

      const deputateLessonUnit = lessonsForPerson
        .filter((lesson) => lesson.subjectContainerUuid === undefined || lesson.subjectContainerUuid === null)
        .map((lesson) => lesson.deputat ?? 0)
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

      const contract = contractData?.contracts.find((c) => c.person.uuid === person.uuid);
      const billedHours = _evalBillingMethod({
        deputateLesson: deputateLessonUnit + deputateSubjectContainer,
        deputateManually: deputateAdditional,
        deputateContract: contract?.hoursWeekly ?? 0,
        billingMethod: contract?.billingMethod ?? BillingMethods.GivenHours,
        limitedToFullTime: contract?.limitedToFullTime ?? false,
        fullTimeHours: fullTimeHours ?? 0,
      });

      return {
        teacherUuid: person.uuid,
        name: person.listName ?? '',
        teachingBlockWeeks,
        teachingBlocks,
        deputateAdditional,
        deputateLessonUnit,
        deputateSubjectContainer,
        contractHours: contract?.hoursWeekly ?? 0,
        billedHours: billedHours,
      };
    }) ?? [];

  const columns: TableColumns<DeputatesTableType>[] = [
    {
      header: t('persons.title', { count: 1 }),
      id: 'name',
      accessorKey: 'name',
      size: 300,
    },
    {
      header: t('common.week', { count: 2 }),
      id: 'teachingBlockWeeks',
      accessorKey: 'teachingBlockWeeks',
      size: 75,
      alignment: 'right',
      accessorFn: ({ teachingBlockWeeks }) => teachingBlockWeeks.toString(),
    },
    {
      header: t('common.epochs'),
      accessorKey: 'teachingBlocks',
      id: 'teachingBlocks',
      size: 75,
      alignment: 'right',
      accessorFn: ({ teachingBlocks }) => teachingBlocks.toString(),
    },
    {
      header: t('deputate.deputateEpoch'),
      accessorFn: (row) => formatNumber(row.deputateSubjectContainer),
      id: 'deputateSubjectContainer',
      size: 100,
      alignment: 'right',
    },
    {
      header: t('deputate.deputateSubject'),
      accessorFn: (row) => formatNumber(row.deputateLessonUnit),
      id: 'deputateLessonUnit',
      size: 100,
      alignment: 'right',
    },
    {
      header: t('deputate.deputateManually'),
      accessorFn: (row) => formatNumber(row.deputateAdditional),
      id: 'deputateAdditional',
      size: 100,
      alignment: 'right',
    },
    {
      header: t('common.sum'),
      accessorFn: (row) => formatNumber(row.deputateAdditional + row.deputateSubjectContainer + row.deputateLessonUnit),
      size: 75,
      id: 'sum',
      alignment: 'right',
    },
    {
      header: t('contracts.contract'),
      accessorFn: (row) => formatNumber(row.contractHours),
      size: 75,
      id: 'contractHours',
      alignment: 'right',
    },
    {
      header: t('common.status'),
      accessorFn: (row) => {
        const sum = row.deputateAdditional + row.deputateSubjectContainer + row.deputateLessonUnit;
        if (sum === null || sum === 0 || row.contractHours === null || row.contractHours === 0) {
          return '--';
        }
        const status = Math.floor((sum / row.contractHours) * 100);
        return status + '%';
      },
      cell: ({ row }) => {
        const sum =
          row.original.deputateAdditional + row.original.deputateSubjectContainer + row.original.deputateLessonUnit;
        if (sum === null || sum === 0 || row.original.contractHours === null || row.original.contractHours === 0) {
          return <>--</>;
        }
        const status = Math.floor((sum / row.original.contractHours) * 100);
        return (
          <span
            style={{
              color:
                status > 100 ? 'var(--color-error)' : status < 100 ? 'var(--color-warning)' : 'var(--color-success)',
            }}
          >
            {status}%
          </span>
        );
      },
      size: 75,
      id: 'status',
      alignment: 'right',
      accessorKey: 'status',
    },
    {
      header: t('deputate.payed'),
      accessorFn: (row) => formatNumber(row.billedHours),
      size: 75,
      id: 'billedHours',
      alignment: 'right',
    },
  ];

  return (
    <Table<DeputatesTableType>
      showBorderRadius
      showRowHover
      showShadow
      columns={columns}
      data={data}
      sorting={sorting}
      onSortingChange={saveSorting}
      isOnWhite={false}
      showVisibility
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={saveColumnVisibility}
      showActionBar
      actionBarSettings={{ showPrintButton: true }}
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('routes.deputate.evaluate.planned.title')} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
        filename: `${t('routes.deputate.evaluate.planned.title')}_${currentSchoolYear?.shortName}`,
      }}
      showSelect={false}
      onRowClick={(e, row) => {
        onNavigate(row.original.teacherUuid);
      }}
      lastColWidth='46px'
      lastCol={(row) => {
        return (
          <Button
            hierarchy='ghost'
            onClick={() => onNavigate(row.original.teacherUuid)}
            icon={<ArrowRightIcon className='small' />}
          />
        );
      }}
    />
  );
});
